import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';

export type SimClockResponse = {
  simDateYmd: string;
  virtualEpochMs: number;
  speed: number;
  playing: boolean;
  updatedAt: string;
  sessionId: string | null;
  source: 'session' | 'wall';
};

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function wallYmd() {
  return new Date().toISOString().slice(0, 10);
}

/** Detecta contexto de simulação / cockpit (JWT, storage ou iframe). */
export function detectSimulationContext(
  user?: {
    isSimulation?: boolean;
    simulationSessionId?: string | null;
  } | null,
): boolean {
  if (user?.isSimulation || user?.simulationSessionId) return true;
  if (typeof sessionStorage !== 'undefined') {
    if (sessionStorage.getItem('ac_sim_session')) return true;
    if (sessionStorage.getItem('ac_cockpit_embed') === '1') return true;
    if (sessionStorage.getItem('sim_cockpit') === '1') return true;
    if (sessionStorage.getItem('sim_session_id')) return true;
  }
  if (typeof localStorage !== 'undefined') {
    if (localStorage.getItem('ac_sim_cockpit') === '1') return true;
    if (localStorage.getItem('ac_sim_session_id')) return true;
    if (localStorage.getItem('ac_sim_auth') === '1') return true;
  }
  try {
    if (typeof window !== 'undefined' && window.self !== window.top) return true;
  } catch {
    return true;
  }
  return false;
}

/**
 * Relógio da sessão de simulação (quando JWT tem isSimulation / simulationSessionId
 * ou quando a UI roda embutida no cockpit /simulador).
 * Fallback: calendário civil.
 */
export function useSimClock(options?: { enabled?: boolean; pollMs?: number }) {
  const user = useAuthStore((s) => s.user) as
    | { isSimulation?: boolean; simulationSessionId?: string | null }
    | null;
  const isSim = detectSimulationContext(user);
  const queryClient = useQueryClient();

  const enabled = options?.enabled ?? isSim;
  const pollMs = options?.pollMs ?? 8_000;
  const sessionKey =
    user?.simulationSessionId ??
    (typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem('ac_sim_session')
      : null) ??
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('ac_sim_session_id')
      : null) ??
    'me';
  const clockKey = ['simulation-clock', sessionKey] as const;

  const query = useQuery({
    queryKey: clockKey,
    queryFn: async () => {
      const { data } = await api.get<SimClockResponse>('/simulation/clock');
      return data;
    },
    enabled: enabled && !!useAuthStore.getState().isAuthenticated,
    refetchInterval: enabled ? pollMs : false,
    staleTime: 3_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  // Cockpit avisa mudança de dia/turno via postMessage — atualiza clock + refetch dashboards.
  useEffect(() => {
    if (!enabled) return;
    const key = ['simulation-clock', sessionKey] as const;
    const onMsg = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'ac_sim_clock') return;
      const ymd = event.data.simDateYmd;
      if (typeof ymd !== 'string' || !YMD_RE.test(ymd)) return;
      const prev = queryClient.getQueryData<SimClockResponse>(key);
      queryClient.setQueryData<SimClockResponse>(key, {
        simDateYmd: ymd,
        virtualEpochMs:
          typeof event.data.virtualEpochMs === 'number'
            ? event.data.virtualEpochMs
            : (prev?.virtualEpochMs ?? Date.now()),
        speed:
          typeof event.data.speed === 'number' ? event.data.speed : (prev?.speed ?? 1),
        playing:
          typeof event.data.playing === 'boolean'
            ? event.data.playing
            : (prev?.playing ?? false),
        updatedAt: new Date().toISOString(),
        sessionId: prev?.sessionId ?? null,
        source: 'session',
      });
      // Refetch painéis do dia (keys com YMD ou prefixes operacionais do dashboard).
      void queryClient.invalidateQueries({
        predicate: (q) => {
          const head = q.queryKey[0];
          if (typeof head === 'string') {
            if (
              head === 'chamadas' ||
              head === 'chamadas-status-dia' ||
              head === 'turno-ao-vivo-horarios' ||
              head === 'presencas-diarias' ||
              head === 'incidentes' ||
              head === 'justificativas' ||
              head === 'portaria-ao-vivo' ||
              head === 'horarios' ||
              head.startsWith('equipe-')
            ) {
              return true;
            }
          }
          return q.queryKey.some(
            (part) => typeof part === 'string' && YMD_RE.test(part),
          );
        },
      });
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [enabled, queryClient, sessionKey]);

  const apiYmd =
    query.data?.simDateYmd && YMD_RE.test(query.data.simDateYmd)
      ? query.data.simDateYmd
      : null;
  const apiSource = query.data?.source;

  // Em simulação: preferir clock da sessão; não disparar queries com data civil
  // enquanto o /simulation/clock ainda não respondeu (evita 2026 vs dia simulado).
  let simDateYmd: string;
  let clockReady: boolean;
  if (!isSim) {
    simDateYmd = wallYmd();
    clockReady = true;
  } else if (apiSource === 'session' && apiYmd) {
    simDateYmd = apiYmd;
    clockReady = true;
  } else if (!query.isFetched || query.isLoading) {
    simDateYmd = '';
    clockReady = false;
  } else {
    // Clock respondeu wall (sem sessão amarrada) — usa YMD retornado/civil
    simDateYmd = apiYmd || wallYmd();
    clockReady = true;
  }

  return {
    ...query,
    isSimulation: isSim,
    clockReady,
    simDateYmd,
    virtualEpochMs: query.data?.virtualEpochMs ?? Date.now(),
    speed: query.data?.speed ?? 1,
    playing: query.data?.playing ?? false,
    sessionId:
      query.data?.sessionId ??
      user?.simulationSessionId ??
      (typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('ac_sim_session')
        : null) ??
      null,
    source: query.data?.source ?? (isSim ? 'session' : 'wall'),
  };
}

/** Data de referência para queries de “hoje” (sim ou civil). */
export function useHojeReferencia() {
  const { simDateYmd, isSimulation, source, clockReady, isFetching, isLoading } =
    useSimClock();
  const civil = wallYmd();

  const hojeStr =
    isSimulation || source === 'session'
      ? simDateYmd || '' // vazio = ainda sincronizando
      : civil;

  return {
    hojeStr,
    isSimulation,
    source,
    /** false enquanto o embed/sim ainda não recebeu o dia da sessão */
    clockReady: isSimulation ? clockReady && YMD_RE.test(hojeStr) : true,
    isSyncing: isSimulation && (!clockReady || isLoading || isFetching),
  };
}
